import { Card, Col, DatePicker, Empty, Row, Statistic, Table, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import { inventoryApi, type VendorAgingRow } from '@/api/inventory';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const PKR = (n: number | string | null | undefined) =>
  n === null || n === undefined ? '—' : Number(n).toLocaleString('en-PK', { maximumFractionDigits: 2 });

export function VendorAgingPage() {
  const [asOf, setAsOf] = useState<Dayjs>(dayjs());
  const params = asOf.format('YYYY-MM-DD');

  const report = useQuery({
    queryKey: ['inventory', 'vendor-aging', params],
    queryFn: () => inventoryApi.vendorAging(params),
  });

  const data = report.data;

  const columns: TableProps<VendorAgingRow>['columns'] = [
    {
      title: 'Vendor',
      dataIndex: 'vendorName',
      width: 220,
      fixed: 'left' as const,
      render: (v: string, row) => (
        <div>
          <Typography.Text strong>{v}</Typography.Text>
          {row.vendorTypeName && (
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {row.vendorTypeName}
              </Typography.Text>
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Oldest open',
      dataIndex: 'oldestOpenAgeDays',
      width: 130,
      render: (days: number | null, row) =>
        days === null || row.oldestOpenInvoiceDate === null ? (
          '—'
        ) : (
          <div>
            <Tag color={days > 90 ? 'red' : days > 60 ? 'orange' : days > 30 ? 'gold' : 'blue'}>{days} days</Tag>
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                since {dayjs(row.oldestOpenInvoiceDate).format('D MMM YYYY')}
              </Typography.Text>
            </div>
          </div>
        ),
    },
    {
      title: '0 – 30 days',
      dataIndex: 'current',
      align: 'right' as const,
      width: 130,
      render: (v: string) => (Number(v) > 0 ? PKR(v) : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: '31 – 60 days',
      dataIndex: 'days31to60',
      align: 'right' as const,
      width: 130,
      render: (v: string) => (Number(v) > 0 ? PKR(v) : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: '61 – 90 days',
      dataIndex: 'days61to90',
      align: 'right' as const,
      width: 130,
      render: (v: string) => (Number(v) > 0 ? PKR(v) : <Typography.Text type="secondary">—</Typography.Text>),
    },
    {
      title: '90+ days',
      dataIndex: 'over90',
      align: 'right' as const,
      width: 130,
      render: (v: string) =>
        Number(v) > 0 ? (
          <Typography.Text strong style={{ color: '#cf1322' }}>{PKR(v)}</Typography.Text>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Total',
      dataIndex: 'total',
      align: 'right' as const,
      width: 140,
      render: (v: string) => <Typography.Text strong>{PKR(v)}</Typography.Text>,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <PageHeader
        eyebrow="Inventory / Vendors"
        title="Vendor aging"
        description="Outstanding payable per vendor bucketed 0-30 / 31-60 / 61-90 / 91+ days. FIFO-applies payments to oldest invoices first."
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/vendor-aging.xlsx',
              params: { asOf: params },
              fileName: `vendor-aging-${params}.xlsx`,
              disabled: !data,
            }]}
          />
        }
      />

      <Card>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={6}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              As of
            </Typography.Text>
            <DatePicker
              style={{ width: '100%' }}
              value={asOf}
              onChange={(v) => v && setAsOf(v)}
              allowClear={false}
            />
          </Col>
        </Row>
      </Card>

      {data && (
        <Card>
          <Row gutter={[16, 12]}>
            <Col xs={12} md={5}>
              <Statistic title="Current (0–30)" value={PKR(data.totalCurrent)} prefix="₨" />
            </Col>
            <Col xs={12} md={5}>
              <Statistic
                title="31 – 60 days"
                value={PKR(data.total31to60)}
                prefix="₨"
                styles={{ content: { color: '#d48806' } }}
              />
            </Col>
            <Col xs={12} md={5}>
              <Statistic
                title="61 – 90 days"
                value={PKR(data.total61to90)}
                prefix="₨"
                styles={{ content: { color: '#d4380d' } }}
              />
            </Col>
            <Col xs={12} md={5}>
              <Statistic
                title="90+ days"
                value={PKR(data.totalOver90)}
                prefix="₨"
                styles={{ content: { color: '#cf1322' } }}
              />
            </Col>
            <Col xs={24} md={4}>
              <Statistic title="Total payable" value={PKR(data.grandTotal)} prefix="₨" />
            </Col>
          </Row>
        </Card>
      )}

      {data && data.rows.length === 0 ? (
        <Card>
          <Empty description="No outstanding vendor payable as of this date." />
        </Card>
      ) : (
        <Card>
          <Table<VendorAgingRow>
            rowKey="vendorId"
            dataSource={data?.rows ?? []}
            loading={report.isFetching}
            columns={columns}
            pagination={{ pageSize: 50, showSizeChanger: true }}
            scroll={{ x: 1100 }}
            size="small"
          />
        </Card>
      )}
    </div>
  );
}
